import type { Hotspot } from "../manifest/schema.js";
import type { HotspotIcon } from "../manifest/schema.js";
import { svgIcon } from "./dom.js";

/** Full circle as path data (the icon set is pure stroked paths so it shares one style). */
const c = (cx: number, cy: number, r: number): string => `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;

/**
 * The in-scene icon set: 24x24, 1.8px gold strokes, round caps. Drawn here (no icon font or CDN) so it stays
 * consistent and works offline. Add a name to `hotspotIcons` in schema.ts and a drawing here together.
 */
export const ICON_PATHS: Record<HotspotIcon, readonly string[]> = {
  lift: ["M6 3h12v18H6z", "M12 3v18", "M7.5 10l1.5-2 1.5 2", "M13.5 14l1.5 2 1.5-2"],
  stairs: ["M3 20h4v-4h4v-4h4V8h4V4"],
  door: ["M4 21h16", "M7 21V3h10v18", "M14 12v.01"],
  exit: ["M13 3H5v18h8", "M10 12h11", "M17 8l4 4-4 4"],
  home: ["M3 11l9-8 9 8", "M5 9.5V21h14V9.5", "M10 21v-6h4v6"],
  shop: ["M3 9l2-5h14l2 5", "M3 9h18", "M5 9v12h14V9", "M10 21v-6h4v6"],
  bag: ["M5 8h14l-1 13H6z", "M9 8V6a3 3 0 0 1 6 0v2"],
  till: ["M3 6h18v12H3z", "M3 10h18", "M6 15h4"],
  table: ["M3 10h18", "M5 10v10", "M19 10v10", "M8 7h8"],
  telescope: ["M4 14l11-5 2 4-11 5z", "M10 16l-2 5", "M11 16l3 5", "M15 9l3-1.5 2 4-3 1.5"],
  chair: ["M7 3v18", "M7 13h10v8", "M7 3h4"],
  stand: [c(12, 4.5, 1.8), "M12 7v7", "M12 14l-3 7", "M12 14l3 7", "M8 10h8"],
  sofa: ["M4 12V9a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3", "M2 12h20v5H2z", "M4 17v3", "M20 17v3"],
  view: ["M3 18h18", "M7 18a5 5 0 0 1 10 0", "M12 6v3", "M5 11l2 1.2", "M19 11l-2 1.2"],
  leaf: ["M5 19c0-9 6-14 14-14 0 8-5 14-14 14z", "M5 19l8-8"],
  car: ["M3 15l2-5h14l2 5v3H3z", "M6 18v2", "M18 18v2", "M7 14.5h.01", "M17 14.5h.01"],
  trophy: ["M8 4h8v5a4 4 0 0 1-8 0z", "M8 6H5a3 3 0 0 0 3 4", "M16 6h3a3 3 0 0 1-3 4", "M12 13v4", "M8 21h8", "M10 17h4v4"],
  flag: ["M5 21V4", "M5 4h13l-3 4 3 4H5"],
  gear: [c(12, 12, 3), "M12 3v3", "M12 18v3", "M3 12h3", "M18 12h3", "M5.6 5.6l2.1 2.1", "M16.3 16.3l2.1 2.1", "M5.6 18.4l2.1-2.1", "M16.3 7.7l2.1-2.1"],
  key: [c(7.5, 12, 3.5), "M11 12h10", "M18 12v3", "M15 12v2"],
  pencil: ["M4 20l1-5L16 4l4 4L9 19z", "M14 6l4 4"],
  sliders: ["M4 6h16", "M4 12h16", "M4 18h16", "M9 4v4", "M15 10v4", "M7 16v4"],
  info: [c(12, 12, 9), "M12 11v6", "M12 7.5v.01"],
  map: ["M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z", "M9 3v15", "M15 6v15"],
  tag: ["M3 12V3h9l9 9-9 9z", "M7.5 7.5h.01"]
};

const has = (s: string, ...xs: string[]): boolean => xs.some((x) => s.includes(x));

/** Icon for a navigate hotspot, inferred from where it goes (`to`) and its id. Undefined = a plain arrow. */
export function inferNavigateIcon(to: string, id = ""): HotspotIcon | undefined {
  if (to === "lift") return "lift";
  if (to === "escalator") return "stairs";
  if (id === "sit-down" || to.endsWith("-table") && to.startsWith("terrace")) return "chair";
  if (id === "stand-up") return "stand";
  if (to.endsWith("-telescope")) return "telescope";
  if (has(to, "design-table")) return "table";
  if (to.endsWith("-browse")) return "bag";
  if (to.endsWith("-till")) return "till";
  if (to.endsWith("-exterior")) return "exit";
  if (id === "enter") return "door";
  if (to === "atrium" || to === "grand-entrance") return "home";
  if (to.endsWith("-arcade") || to === "gallery") return "shop";
  if (has(to, "configurator")) return "sliders";
  if (has(to, "design-studio")) return "pencil";
  if (has(to, "heritage")) return "trophy";
  if (has(to, "racing-room")) return "flag";
  if (has(to, "engine-room")) return "gear";
  if (has(to, "delivery-bay")) return "key";
  if (has(to, "lounge")) return "sofa";
  if (has(to, "showroom")) return "car";
  if (has(to, "garden")) return "leaf";
  if (to.startsWith("terrace")) return "view";
  if (has(to, "workshop")) return "door";
  if (to.endsWith("-floor")) return "shop";
  return undefined;
}

const KIND_ICON: Partial<Record<Hotspot["kind"], HotspotIcon>> = {
  product: "tag",
  info: "info",
  directory: "map",
  checkout: "till",
  configurator: "sliders",
  telescope: "telescope",
  "retailer-config": "table",
  menu: "lift"
};

/** The icon a hotspot shows: its explicit `icon`, otherwise a default from its kind or destination. */
export function hotspotIcon(hs: Hotspot): HotspotIcon | undefined {
  if (hs.kind === "clue") return undefined; // clues stay hidden on purpose
  if ("icon" in hs && hs.icon) return hs.icon;
  if (hs.kind === "navigate") return inferNavigateIcon(hs.to, hs.id);
  return KIND_ICON[hs.kind];
}

export function iconSvg(name: HotspotIcon): SVGSVGElement {
  return svgIcon(ICON_PATHS[name], { class: `hs-icon hs-icon--${name}` });
}
